import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api.js';
import { 
  ShoppingBag, 
  Loader2, 
  ShoppingCart, 
  User, 
  LogIn, 
  LogOut,
  Plus,
  Minus,
  X,
  Mail,
  Phone,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Heart,
  Star,
  Sparkles
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PublicWebShop = () => {
  const { slug } = useParams();
  const [webshop, setWebshop] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [customer, setCustomer] = useState(null);
  const [cart, setCart] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [paymentData, setPaymentData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: ''
    },
    notes: ''
  });

  // Login/Register form state
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: ''
  });

  // Load webshop and products
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Učitaj prvo webshop (brže), zatim proizvode
        const webshopRes = await axios.get(`${API_BASE_URL}/api/webshop/public/${slug}`);
        setWebshop(webshopRes.data);
        
        // Učitaj proizvode nakon webshop-a sa timeout-om
        const productsRes = await axios.get(`${API_BASE_URL}/api/webshop/public/${slug}/products`, {
          timeout: 15000
        });
        setProducts(productsRes.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        if (err.response?.status === 404) {
          setError('Web shop nije pronađen');
        } else {
          setError(err.response?.data?.message || 'Greška pri učitavanju web shopa');
        }
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchData();
    }
  }, [slug]);

  // Load customer from localStorage
  useEffect(() => {
    const savedCustomer = localStorage.getItem(`webshop_customer_${slug}`);
    const savedToken = localStorage.getItem(`webshop_token_${slug}`);
    if (savedCustomer && savedToken) {
      setCustomer(JSON.parse(savedCustomer));
      // Set token for future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
    }
  }, [slug]);

  // Load cart from localStorage
  useEffect(() => {
    const savedCart = localStorage.getItem(`webshop_cart_${slug}`);
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, [slug]);

  // Save cart to localStorage
  useEffect(() => {
    if (cart.length > 0) {
      localStorage.setItem(`webshop_cart_${slug}`, JSON.stringify(cart));
    }
  }, [cart, slug]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_BASE_URL}/api/webshop/public/${slug}/login`, loginData);
      const { token, customer: customerData } = response.data;
      
      localStorage.setItem(`webshop_token_${slug}`, token);
      localStorage.setItem(`webshop_customer_${slug}`, JSON.stringify(customerData));
      setCustomer(customerData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      toast.success('Uspešno ste se prijavili');
      setShowLogin(false);
      setLoginData({ email: '', password: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Greška pri prijavi');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_BASE_URL}/api/webshop/public/${slug}/register`, registerData);
      const { token, customer: customerData } = response.data;
      
      localStorage.setItem(`webshop_token_${slug}`, token);
      localStorage.setItem(`webshop_customer_${slug}`, JSON.stringify(customerData));
      setCustomer(customerData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      toast.success('Uspešno ste se registrovali');
      setShowRegister(false);
      setRegisterData({ email: '', password: '', firstName: '', lastName: '', phone: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Greška pri registraciji');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(`webshop_token_${slug}`);
    localStorage.removeItem(`webshop_customer_${slug}`);
    setCustomer(null);
    delete axios.defaults.headers.common['Authorization'];
    toast.success('Uspešno ste se odjavili');
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.productId === product._id);
    const currentQuantity = existingItem ? existingItem.quantity : 0;
    const newQuantity = currentQuantity + 1;
    
    // Proveri da li ima dovoljno na stanju
    if (newQuantity > product.quantity) {
      toast.error(`Nema dovoljno proizvoda na stanju. Dostupno: ${product.quantity} kom`);
      return;
    }
    
    if (existingItem) {
      setCart(cart.map(item =>
        item.productId === product._id
          ? { ...item, quantity: newQuantity }
          : item
      ));
    } else {
      setCart([...cart, {
        productId: product._id,
        name: product.name,
        price: product.price,
        image: product.image,
        quantity: 1
      }]);
    }
    toast.success('Proizvod dodat u korpu');
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.productId !== productId));
    toast.success('Proizvod uklonjen iz korpe');
  };

  const updateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    // Pronađi proizvod u listi proizvoda da proverimo zalihu
    const product = products.find(p => p._id === productId);
    if (product && quantity > product.quantity) {
      toast.error(`Nema dovoljno proizvoda na stanju. Dostupno: ${product.quantity} kom`);
      return;
    }
    
    setCart(cart.map(item =>
      item.productId === productId
        ? { ...item, quantity }
        : item
    ));
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const toggleFavorite = (productId) => {
    if (favorites.includes(productId)) {
      setFavorites(favorites.filter(id => id !== productId));
      toast.success('Proizvod uklonjen iz omiljenih');
    } else {
      setFavorites([...favorites, productId]);
      toast.success('Proizvod dodat u omiljene');
    }
  };

  const isFavorite = (productId) => {
    return favorites.includes(productId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Učitavanje...</p>
        </div>
      </div>
    );
  }

  if (error || !webshop) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Web shop nije pronađen</h1>
          <p className="text-gray-600">{error || 'Web shop sa ovim linkom ne postoji'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-700 border-b border-blue-800 sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <ShoppingBag className="w-8 h-8 text-white" />
              <h1 className="text-2xl font-bold text-white">{webshop.shopName}</h1>
            </div>
            
            <div className="flex items-center gap-4">
              {customer ? (
                <>
                  <div className="flex items-center gap-2 text-white">
                    <User className="w-5 h-5" />
                    <span>{customer.firstName} {customer.lastName}</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-md transition-colors backdrop-blur-sm"
                  >
                    <LogOut className="w-4 h-4" />
                    Odjavi se
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => { setShowLogin(true); setShowRegister(false); }}
                    className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-md transition-colors backdrop-blur-sm"
                  >
                    <LogIn className="w-4 h-4" />
                    Prijavi se
                  </button>
                  <button
                    onClick={() => { setShowRegister(true); setShowLogin(false); }}
                    className="px-4 py-2 bg-white hover:bg-gray-100 text-blue-700 rounded-md transition-colors font-semibold"
                  >
                    Registruj se
                  </button>
                </>
              )}
              
              <button
                onClick={() => setShowCart(!showCart)}
                className="relative flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-100 text-blue-700 rounded-md transition-colors font-semibold"
              >
                <ShoppingCart className="w-5 h-5" />
                Korpa
                {getCartItemCount() > 0 && (
                  <span className="absolute -top-2 -right-2 bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {getCartItemCount()}
                  </span>
                )}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-blue-500/30">
            <p className="text-blue-100 text-sm flex items-center gap-1">
              <Sparkles className="w-4 h-4" />
              Powered by <span className="font-semibold text-white">SpinTasker</span>
            </p>
            {favorites.length > 0 && (
              <button
                onClick={() => {
                  const favoriteProducts = products.filter(p => favorites.includes(p._id));
                  if (favoriteProducts.length > 0) {
                    // Scroll to favorites section or show modal
                    toast.info(`Imate ${favorites.length} omiljenih proizvoda`);
                  }
                }}
                className="text-blue-100 hover:text-white text-sm flex items-center gap-1 transition-colors"
              >
                <Heart className="w-4 h-4 fill-current" />
                <span>Omiljeni ({favorites.length})</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content - Products */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Dobro došli na {webshop.shopName}
          </h2>
          {webshop.shopDescription && (
            <p className="text-gray-600 max-w-2xl mx-auto">
              {webshop.shopDescription}
            </p>
          )}
        </div>

        {/* Products Grid */}
        {products.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product) => {
              const displayImage = (product.images && product.images.length > 0) 
                ? product.images[0] 
                : (product.image || '');
              
              return (
                <div 
                  key={product._id} 
                  className="bg-white border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 group relative"
                >
                  {/* Favorite Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(product._id);
                    }}
                    className="absolute top-3 right-3 z-10 p-2 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:scale-110"
                    title={isFavorite(product._id) ? 'Ukloni iz omiljenih' : 'Dodaj u omiljene'}
                  >
                    <Heart 
                      className={`w-5 h-5 transition-colors ${
                        isFavorite(product._id) 
                          ? 'text-red-500 fill-red-500' 
                          : 'text-gray-400 hover:text-red-500'
                      }`} 
                    />
                  </button>

                  {/* Product Image */}
                  <div
                    className="cursor-pointer"
                    onClick={() => {
                      setSelectedProduct(product);
                      setSelectedImageIndex(0);
                    }}
                  >
                    {displayImage ? (
                      <div className="relative overflow-hidden bg-gray-100">
                        <img 
                          src={displayImage} 
                          alt={product.name} 
                          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center" style={{ display: 'none' }}>
                          <ShoppingBag className="w-16 h-16 text-gray-400" />
                        </div>
                        {product.quantity === 0 && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <span className="bg-red-500 text-white px-4 py-2 rounded-md font-semibold">Nema na stanju</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="w-full h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                        <ShoppingBag className="w-16 h-16 text-gray-400" />
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    <h3 
                      className="text-lg font-semibold text-gray-900 mb-2 cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={() => {
                        setSelectedProduct(product);
                        setSelectedImageIndex(0);
                      }}
                    >
                      {product.name}
                    </h3>
                    {product.description && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.description}</p>
                    )}
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl font-bold text-blue-600">
                        {product.price} {webshop.currency}
                      </span>
                      {product.quantity > 0 ? (
                        <span className="text-sm text-green-600 font-medium bg-green-50 px-2 py-1 rounded">
                          ✓ Na stanju: {product.quantity}
                        </span>
                      ) : (
                        <span className="text-sm text-red-500 font-medium bg-red-50 px-2 py-1 rounded">
                          Nema na stanju
                        </span>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        addToCart(product);
                      }}
                      disabled={product.quantity === 0}
                      className="w-full px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white rounded-md transition-all font-semibold shadow-md hover:shadow-lg transform hover:scale-105 disabled:transform-none"
                    >
                      Dodaj u korpu
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
            <ShoppingBag className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">Nema proizvoda</h3>
            <p className="text-gray-600">Proizvodi će biti dostupni uskoro.</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-blue-600 to-blue-700 border-t border-blue-800 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" />
                {webshop.shopName}
              </h3>
              {webshop.shopDescription && (
                <p className="text-blue-100 text-sm">{webshop.shopDescription}</p>
              )}
            </div>
            <div>
              <h4 className="text-md font-semibold text-white mb-4">Kontakt</h4>
              <div className="space-y-2 text-sm text-blue-100">
                {webshop.shopEmail && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <span>{webshop.shopEmail}</span>
                  </div>
                )}
                {webshop.shopPhone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <span>{webshop.shopPhone}</span>
                  </div>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-md font-semibold text-white mb-4">Informacije</h4>
              <div className="space-y-2 text-sm text-blue-100">
                <div>Valuta: {webshop.currency}</div>
                {webshop.taxRate !== undefined && <div>Porez: {webshop.taxRate}%</div>}
                {webshop.shippingEnabled && <div>Dostava: Omogućena</div>}
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-blue-500/30 text-center">
            <p className="text-blue-100 text-sm mb-2">
              © {new Date().getFullYear()} {webshop.shopName}. Sva prava zadržana.
            </p>
            <p className="text-blue-200 text-xs flex items-center justify-center gap-1">
              <Sparkles className="w-3 h-3" />
              Powered by <span className="font-semibold text-white">SpinTasker</span>
            </p>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      {showLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Prijavi se</h2>
              <button onClick={() => setShowLogin(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lozinka</label>
                <input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
              >
                Prijavi se
              </button>
            </form>
            <p className="mt-4 text-sm text-gray-600 text-center">
              Nemate nalog?{' '}
              <button
                onClick={() => { setShowRegister(true); setShowLogin(false); }}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Registruj se
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Register Modal */}
      {showRegister && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Registruj se</h2>
              <button onClick={() => setShowRegister(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Ime</label>
                <input
                  type="text"
                  value={registerData.firstName}
                  onChange={(e) => setRegisterData({ ...registerData, firstName: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Prezime</label>
                <input
                  type="text"
                  value={registerData.lastName}
                  onChange={(e) => setRegisterData({ ...registerData, lastName: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                <input
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Telefon</label>
                <input
                  type="tel"
                  value={registerData.phone}
                  onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Lozinka</label>
                <input
                  type="password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  minLength={6}
                />
              </div>
              <button
                type="submit"
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
              >
                Registruj se
              </button>
            </form>
            <p className="mt-4 text-sm text-gray-400 text-center">
              Već imate nalog?{' '}
              <button
                onClick={() => { setShowLogin(true); setShowRegister(false); }}
                className="text-blue-400 hover:text-blue-300"
              >
                Prijavi se
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end z-50">
          <div className="bg-gray-800 w-full max-w-md h-full overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">Korpa</h2>
              <button onClick={() => setShowCart(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {cart.length > 0 ? (
                <>
                  <div className="space-y-4 mb-6">
                    {cart.map((item) => (
                      <div key={item.productId} className="flex items-center gap-4 bg-gray-700 p-4 rounded-lg">
                        {item.image && (
                          <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded" />
                        )}
                        <div className="flex-1">
                          <h3 className="text-white font-medium">{item.name}</h3>
                          <p className="text-blue-400">{item.price} {webshop.currency}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateCartQuantity(item.productId, item.quantity - 1)}
                            className="p-1 bg-gray-600 hover:bg-gray-500 rounded text-white"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="text-white w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateCartQuantity(item.productId, item.quantity + 1)}
                            className="p-1 bg-gray-600 hover:bg-gray-500 rounded text-white"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.productId)}
                          className="p-1 text-red-400 hover:text-red-300"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-gray-700 pt-4">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-lg font-semibold text-white">Ukupno:</span>
                      <span className="text-xl font-bold text-blue-400">
                        {getCartTotal().toFixed(2)} {webshop.currency}
                      </span>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button
                        className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors font-semibold"
                        onClick={() => toast.info('Funkcionalnost plaćanja karticom će biti dostupna uskoro')}
                      >
                        Nastavi na plaćanje
                      </button>
                      <button
                        className="w-full px-4 py-3 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors font-semibold"
                        onClick={() => {
                          if (customer) {
                            // Pre-fill podaci iz customer profila
                            setPaymentData({
                              firstName: customer.firstName || '',
                              lastName: customer.lastName || '',
                              email: customer.email || '',
                              phone: customer.phone || '',
                              address: customer.address || {
                                street: '',
                                city: '',
                                postalCode: '',
                                country: ''
                              },
                              notes: ''
                            });
                          }
                          setShowPaymentForm(true);
                        }}
                      >
                        Plaćanje pouzećem
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <ShoppingCart className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Korpa je prazna</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold text-white">{selectedProduct.name}</h2>
              <button
                onClick={() => {
                  setSelectedProduct(null);
                  setSelectedImageIndex(0);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {/* Images Gallery */}
              {selectedProduct.images && selectedProduct.images.length > 0 ? (
                <div className="mb-6">
                  <div className="relative mb-4">
                    <img
                      src={selectedProduct.images[selectedImageIndex]}
                      alt={selectedProduct.name}
                      className="w-full h-96 object-contain bg-gray-700 rounded-lg"
                      onError={(e) => {
                        e.target.src = '';
                        e.target.style.display = 'none';
                      }}
                    />
                    {selectedProduct.images.length > 1 && (
                      <>
                        <button
                          onClick={() => setSelectedImageIndex((prev) => 
                            prev > 0 ? prev - 1 : selectedProduct.images.length - 1
                          )}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full transition-all"
                        >
                          <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button
                          onClick={() => setSelectedImageIndex((prev) => 
                            prev < selectedProduct.images.length - 1 ? prev + 1 : 0
                          )}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full transition-all"
                        >
                          <ChevronRight className="w-6 h-6" />
                        </button>
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                          {selectedImageIndex + 1} / {selectedProduct.images.length}
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Thumbnail Gallery */}
                  {selectedProduct.images.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {selectedProduct.images.map((image, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImageIndex(index)}
                          className={`flex-shrink-0 w-20 h-20 rounded border-2 overflow-hidden ${
                            selectedImageIndex === index 
                              ? 'border-blue-500' 
                              : 'border-gray-600 hover:border-gray-500'
                          }`}
                        >
                          <img
                            src={image}
                            alt={`${selectedProduct.name} ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = '';
                              e.target.style.display = 'none';
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : selectedProduct.image ? (
                <div className="mb-6">
                  <img
                    src={selectedProduct.image}
                    alt={selectedProduct.name}
                    className="w-full h-96 object-contain bg-gray-700 rounded-lg"
                    onError={(e) => {
                      e.target.src = '';
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              ) : (
                <div className="mb-6 w-full h-96 bg-gray-700 rounded-lg flex items-center justify-center">
                  <ShoppingBag className="w-32 h-32 text-gray-600" />
                </div>
              )}

              {/* Product Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-4">Informacije o proizvodu</h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-400">SKU:</span>
                      <p className="text-white font-medium">{selectedProduct.sku}</p>
                    </div>
                    {selectedProduct.category && (
                      <div>
                        <span className="text-sm text-gray-400">Kategorija:</span>
                        <p className="text-white font-medium">{selectedProduct.category}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-sm text-gray-400">Cena:</span>
                      <p className="text-2xl font-bold text-blue-400">
                        {selectedProduct.price} {webshop.currency}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-400">Stanje:</span>
                      {selectedProduct.quantity > 0 ? (
                        <p className="text-green-400 font-medium">Na stanju: {selectedProduct.quantity} kom</p>
                      ) : (
                        <p className="text-red-400 font-medium">Nema na stanju</p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-white mb-4">Karakteristike</h3>
                  {selectedProduct.specifications && selectedProduct.specifications.length > 0 ? (
                    <div className="space-y-2">
                      {selectedProduct.specifications.map((spec, index) => (
                        <div key={index} className="flex gap-2 border-b border-gray-700 pb-2">
                          <span className="text-gray-400 font-medium min-w-[120px]">
                            {spec.key}:
                          </span>
                          <span className="text-white">{spec.value}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">Nema dodatih karakteristika</p>
                  )}
                </div>
              </div>

              {/* Napomena */}
              {selectedProduct.description && (
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-white mb-3">Napomena</h3>
                  <p className="text-gray-300 whitespace-pre-wrap">{selectedProduct.description}</p>
                </div>
              )}

              {/* Add to Cart Button */}
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    addToCart(selectedProduct);
                    setSelectedProduct(null);
                  }}
                  disabled={selectedProduct.quantity === 0}
                  className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-md transition-colors font-semibold text-lg"
                >
                  Dodaj u korpu
                </button>
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="px-6 py-3 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors"
                >
                  Zatvori
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Form Modal (Pouzećem) */}
      {showPaymentForm && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold text-white">Plaćanje pouzećem</h2>
              <button
                onClick={() => {
                  setShowPaymentForm(false);
                  setPaymentData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    address: {
                      street: '',
                      city: '',
                      postalCode: '',
                      country: ''
                    },
                    notes: ''
                  });
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form 
              onSubmit={async (e) => {
                e.preventDefault();
                
                // Validacija
                if (!paymentData.firstName.trim() || !paymentData.lastName.trim()) {
                  toast.error('Ime i prezime su obavezni');
                  return;
                }

                if (!paymentData.email.trim()) {
                  toast.error('Email je obavezan');
                  return;
                }

                if (!paymentData.phone.trim()) {
                  toast.error('Telefon je obavezan');
                  return;
                }

                if (!paymentData.address.street.trim() || !paymentData.address.city.trim()) {
                  toast.error('Adresa i grad su obavezni');
                  return;
                }

                try {
                  const orderData = {
                    items: cart.map(item => ({
                      productId: item.productId,
                      name: item.name,
                      price: item.price,
                      quantity: item.quantity
                    })),
                    customerData: {
                      firstName: paymentData.firstName.trim(),
                      lastName: paymentData.lastName.trim(),
                      email: paymentData.email.trim(),
                      phone: paymentData.phone.trim(),
                      address: paymentData.address
                    },
                    paymentMethod: 'cash_on_delivery',
                    notes: paymentData.notes.trim()
                  };

                  const response = await axios.post(
                    `${API_BASE_URL}/api/webshop/public/${slug}/orders`,
                    orderData
                  );

                  toast.success(`Narudžbina #${response.data.orderNumber} je uspešno kreirana! Kontaktiraćemo vas uskoro.`);
                  setShowPaymentForm(false);
                  setCart([]);
                  localStorage.removeItem(`webshop_cart_${slug}`);
                  setShowCart(false);
                } catch (error) {
                  console.error('Error creating order:', error);
                  toast.error(error.response?.data?.message || 'Greška pri kreiranju narudžbine');
                }
              }}
              className="p-6 space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Ime *
                  </label>
                  <input
                    type="text"
                    value={paymentData.firstName}
                    onChange={(e) => setPaymentData({ ...paymentData, firstName: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Prezime *
                  </label>
                  <input
                    type="text"
                    value={paymentData.lastName}
                    onChange={(e) => setPaymentData({ ...paymentData, lastName: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={paymentData.email}
                    onChange={(e) => setPaymentData({ ...paymentData, email: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Telefon *
                  </label>
                  <input
                    type="tel"
                    value={paymentData.phone}
                    onChange={(e) => setPaymentData({ ...paymentData, phone: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Ulica i broj *
                  </label>
                  <input
                    type="text"
                    value={paymentData.address.street}
                    onChange={(e) => setPaymentData({ 
                      ...paymentData, 
                      address: { ...paymentData.address, street: e.target.value }
                    })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Grad *
                  </label>
                  <input
                    type="text"
                    value={paymentData.address.city}
                    onChange={(e) => setPaymentData({ 
                      ...paymentData, 
                      address: { ...paymentData.address, city: e.target.value }
                    })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Poštanski broj
                  </label>
                  <input
                    type="text"
                    value={paymentData.address.postalCode}
                    onChange={(e) => setPaymentData({ 
                      ...paymentData, 
                      address: { ...paymentData.address, postalCode: e.target.value }
                    })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Država
                  </label>
                  <input
                    type="text"
                    value={paymentData.address.country}
                    onChange={(e) => setPaymentData({ 
                      ...paymentData, 
                      address: { ...paymentData.address, country: e.target.value }
                    })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Srbija"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Napomena (opciono)
                  </label>
                  <textarea
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Dodatne napomene za dostavu..."
                  />
                </div>
              </div>

              <div className="border-t border-gray-700 pt-4">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-semibold text-white">Ukupno za plaćanje:</span>
                  <span className="text-xl font-bold text-blue-400">
                    {getCartTotal().toFixed(2)} {webshop.currency}
                  </span>
                </div>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPaymentForm(false);
                      setPaymentData({
                        firstName: '',
                        lastName: '',
                        email: '',
                        phone: '',
                        address: {
                          street: '',
                          city: '',
                          postalCode: '',
                          country: ''
                        },
                        notes: ''
                      });
                    }}
                    className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors"
                  >
                    Otkaži
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors font-semibold"
                  >
                    Potvrdi narudžbinu
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default PublicWebShop;
